/**
 * The signed-in user. `status` starts as "loading" until /api/me answers.
 */
import { create } from 'zustand';
import type { PublicUser } from '@aicaption/shared';
import { ApiError, api, setUnauthorizedHandler } from '../lib/api';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: Status;
  user: PublicUser | null;
  init(): Promise<void>;
  setUser(user: PublicUser): void;
  /** Re-fetch the user (usage counters change after captions are generated). */
  refresh(): Promise<void>;
  logout(): Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,

  async init() {
    try {
      set({ user: await api.me(), status: 'authenticated' });
    } catch (err) {
      if (err instanceof ApiError && err.status !== 401) console.warn('Could not load session', err);
      set({ user: null, status: 'anonymous' });
    }
  },

  setUser: (user) => set({ user, status: 'authenticated' }),

  async refresh() {
    if (get().status !== 'authenticated') return;
    try {
      set({ user: await api.me() });
    } catch {
      // 401s are handled by the unauthorized handler below
    }
  },

  async logout() {
    await api.logout().catch(() => undefined);
    set({ user: null, status: 'anonymous' });
  },
}));

setUnauthorizedHandler(() => useAuth.setState({ user: null, status: 'anonymous' }));

export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]!.toUpperCase())
      .join('') || '?'
  );
}
